import { forwardRef, useEffect, useMemo, useState } from 'react';

import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

import { datePickerFloating } from './DatePicker.css';
import { RangeDatePickerPanel } from './RangeDatePickerPanel';
import { DatePickerPresetGroup, DateRange, RangeDatePickerProps } from './types';
import { useDatePickerTrigger } from './useDatePickerTrigger';
import { createDefaultPresets } from './utils';
import { Button, IconButton } from '../Button';
import { IconCalendarLine, IconXLine } from '../Icon';

const formatDateKey = (date: Date) => format(date, 'yyyy-MM-dd');

// Label을 계산하기 위한 preset groups를 찾는 함수
const findPresetLabel = (range: DateRange | undefined, presetGroups: DatePickerPresetGroup[]) => {
  if (!range?.from || !range.to) return undefined;
  const fromKey = formatDateKey(range.from);
  const toKey = formatDateKey(range.to);

  for (const group of presetGroups) {
    for (const preset of group.items) {
      const presetValue = preset.getValue();
      if (
        presetValue.from &&
        presetValue.to &&
        formatDateKey(presetValue.from) === fromKey &&
        formatDateKey(presetValue.to) === toKey
      ) {
        return preset.label;
      }
    }
  }

  return undefined;
};

const getPresetYear = (range: DateRange | undefined) => {
  return range?.from ? range.from.getFullYear() : new Date().getFullYear();
};

export const RangeDatePicker = forwardRef<HTMLButtonElement, RangeDatePickerProps>((props, ref) => {
  const {
    value,
    onChange,
    placeholder,
    minDate,
    maxDate,
    buttonProps,
    className,
    disabled = false,
    closable = false,
    limitHeightToViewport = false,
    showPresets = true,
    customPresetGroups,
    defaultPresetOptions,
  } = props;

  const {
    isOpen,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    handleClose,
  } = useDatePickerTrigger({ limitHeightToViewport });

  const [presetYear, setPresetYear] = useState(() => getPresetYear(value));
  const shouldShowPresets = showPresets ?? true;

  // Panel에 표시할 preset groups
  const panelPresetGroups = useMemo(() => {
    if (!shouldShowPresets) return [];
    if (customPresetGroups) return customPresetGroups;
    return createDefaultPresets(presetYear, defaultPresetOptions ?? {});
  }, [customPresetGroups, defaultPresetOptions, presetYear, shouldShowPresets]);

  // Label을 계산하기 위한 preset groups
  const labelPresetGroups = useMemo(() => {
    if (!shouldShowPresets || !value?.from) return [];
    if (customPresetGroups) return customPresetGroups;
    return createDefaultPresets(value.from.getFullYear(), defaultPresetOptions ?? {});
  }, [customPresetGroups, defaultPresetOptions, shouldShowPresets, value?.from]);

  useEffect(() => {
    if (!isOpen) return;
    setPresetYear(getPresetYear(value));
  }, [isOpen, value]);

  const formatDisplayValue = () => {
    if (!value?.from) return placeholder || '기간 선택';

    const presetLabel = findPresetLabel(value, labelPresetGroups);
    const fromLabel = format(value.from, 'yy-MM-dd', { locale: ko });
    const toLabel = value.to ? format(value.to, 'yy-MM-dd', { locale: ko }) : '';

    if (presetLabel) return `${presetLabel} (${fromLabel} ~ ${toLabel})`;

    return `${fromLabel} ~ ${toLabel}`;
  };

  return (
    <>
      <Button
        ref={(node) => {
          refs.setReference(node);
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={className}
        disabled={disabled}
        prefix={<IconCalendarLine width={18} height={18} />}
        closable={closable}
        suffix={
          closable ? (
            <IconButton
              buttonType="ghost"
              buttonSize="s"
              icon={<IconXLine />}
              tooltipMessage="초기화"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(undefined);
                handleClose();
              }}
            />
          ) : undefined
        }
        {...buttonProps}
        {...getReferenceProps()}
      >
        {formatDisplayValue()}
      </Button>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal>
            <div
              ref={refs.setFloating}
              className={datePickerFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <RangeDatePickerPanel
                value={value}
                onChange={onChange}
                minDate={minDate}
                maxDate={maxDate}
                showPresets={showPresets}
                presetGroups={panelPresetGroups}
                presetYear={presetYear}
                onPresetYearChange={setPresetYear}
                onClose={handleClose}
                onApply={(range) => {
                  onChange?.(range);
                  handleClose();
                }}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
});

RangeDatePicker.displayName = 'RangeDatePicker';
